import React, { useEffect, useState } from 'react';
import { CalendarDays, Radio, Users } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { EntityManager } from '../components/admin/catalog/EntityManager';
import { EventManager } from '../components/admin/catalog/EventManager';
import { EventSourceManager } from '../components/admin/catalog/EventSourceManager';
import {
  EmptyState,
  ErrorBanner,
  Loading,
  ObjectTab,
  ObjectTabs,
  Workspace,
  errorMessage,
} from '../components/admin/catalog/CatalogUI';
import { catalogApi } from '../services/catalogApi';
import type { CatalogMetadata } from '../types/catalog';

type CatalogView = 'entities' | 'sources' | 'events';

export const AdminCatalogPage: React.FC = () => {
  const [view, setView] = useState<CatalogView>('entities');
  const [metadata, setMetadata] = useState<CatalogMetadata | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [focusEntityId, setFocusEntityId] = useState<number | null>(null);
  const [focusEventSourceId, setFocusEventSourceId] = useState<number | null>(null);
  const [focusEventId, setFocusEventId] = useState<number | null>(null);

  useEffect(() => {
    catalogApi.getMetadata()
      .then(setMetadata)
      .catch((loadError) => setError(errorMessage(loadError)));
  }, []);

  const openEntity = (entityId: number) => {
    setFocusEntityId(entityId);
    setView('entities');
  };

  const openSource = (eventSourceId: number) => {
    setFocusEventSourceId(eventSourceId);
    setView('sources');
  };

  const openEvent = (eventId: number) => {
    setFocusEventId(eventId);
    setView('events');
  };

  return (
    <AdminLayout
      currentPage="catalog"
      pageTitle="Catalog"
      pageDescription="Manage entities, event sources, events, and their relationships."
    >
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <ObjectTabs role="tablist" aria-label="Catalog object type">
        <ObjectTab
          type="button"
          role="tab"
          aria-selected={view === 'entities'}
          $active={view === 'entities'}
          onClick={() => setView('entities')}
        >
          <Users size={17} /> Entities
        </ObjectTab>
        <ObjectTab
          type="button"
          role="tab"
          aria-selected={view === 'sources'}
          $active={view === 'sources'}
          onClick={() => setView('sources')}
        >
          <Radio size={17} /> Event sources
        </ObjectTab>
        <ObjectTab
          type="button"
          role="tab"
          aria-selected={view === 'events'}
          $active={view === 'events'}
          onClick={() => setView('events')}
        >
          <CalendarDays size={17} /> Events
        </ObjectTab>
      </ObjectTabs>

      <Workspace>
        {!metadata ? (
          error ? <EmptyState>Catalog metadata could not be loaded.</EmptyState> : <Loading label="Loading catalog" />
        ) : view === 'entities' ? (
          <EntityManager
            metadata={metadata}
            focusEntityId={focusEntityId}
            onOpenSource={openSource}
            onOpenEvent={openEvent}
          />
        ) : view === 'sources' ? (
          <EventSourceManager
            metadata={metadata}
            focusEventSourceId={focusEventSourceId}
            onOpenEntity={openEntity}
            onOpenEvent={openEvent}
          />
        ) : (
          <EventManager
            focusEventId={focusEventId}
            onOpenEntity={openEntity}
            onOpenSource={openSource}
          />
        )}
      </Workspace>
    </AdminLayout>
  );
};

export default AdminCatalogPage;